/**
 * The offer door — one HTTP pair, every surface.
 *
 * `GET /api/coaching/offer` asks the dealer what to do next; passing a
 * `sessionId` asks the wrap card's question ("what now?") instead of the
 * home's ("what should I do today?").  `POST …/commit` takes one card
 * and returns the sitting it minted.
 *
 * Commit is exactly-once server-side: two taps on the same card mint one
 * sitting and the second returns the first, so a double-click here is a
 * navigation, never a second session.
 */

import type { SessionOffer } from "../types/state";

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : "";
}

async function readOrThrow(response: Response, what: string) {
  if (!response.ok) {
    // The server's own words: an expired offer and an unservable recipe
    // are different problems with different recoveries, and collapsing
    // them into "failed" makes both unrecoverable.
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      detail = (body?.detail ?? body?.message ?? detail) as string;
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new Error(`${what}: ${detail}`);
  }
  return response.json();
}

/** Deal a fresh offer.  With `sessionId`, the sitting that just ended. */
export async function fetchOffer(sessionId?: string): Promise<SessionOffer> {
  const query = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : "";
  const response = await fetch(`/api/coaching/offer${query}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  return (await readOrThrow(response, "offer")) as SessionOffer;
}

/** Take one card.  Returns the session id to navigate to. */
export async function commitOffer(offerId: string, choiceId: string): Promise<string> {
  const response = await fetch(
    `/api/coaching/offers/${encodeURIComponent(offerId)}/commit`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRFToken": csrfToken(),
      },
      body: JSON.stringify({ choiceId }),
    }
  );
  const body = (await readOrThrow(response, "commit")) as { sessionId: string };
  return body.sessionId;
}
